// The devices this account is signed in on (ICD-S03 §4).
//
// OUT OF `AccountScreen` on 2026-09-17. It was the last reader living inside a
// screen, and it showed: three separate `useState` calls with no identity, no
// abort, and no parse — the response was cast to a hand-written `Device`
// interface that re-declared five fields the contract now carries as
// `SessionRef`. Signing out and signing in as somebody else would have shown
// the first account's devices until the second answer landed, because nothing
// keyed the answer to whose it was.
//
// A failure is reported, never swallowed into an empty list: "nobody is
// signed in anywhere" is a claim, and a request that did not happen is not
// entitled to make it.

import { z } from 'zod';
import { PasswordChanged, SessionRef, type PasswordChangeInput } from '@fsp/domain';
import { send, type Reply } from './api';
import { useRead, type Read } from './read';

const SessionList = z.array(SessionRef);

/**
 * Ask for the list.
 *
 * `userId` is the identity rather than part of the path — `/api/me/sessions`
 * is the same URL for everybody, and the cookie decides whose it is.
 */
export function useDevices(userId: string | null): Read<SessionRef[] | null> {
  return useRead(userId === null ? null : '/me/sessions', SessionList, { key: userId ?? '' });
}

/**
 * Sign one device out.
 *
 * The endpoint answers with what is LEFT, so the caller has the new list
 * without a second round trip and there is no window where the screen and the
 * server disagree.
 */
export function revokeDevice(id: string): Promise<Reply<SessionRef[]>> {
  return send('DELETE', `/me/sessions/${encodeURIComponent(id)}`, undefined, SessionList);
}

/**
 * A new password, given the current one (`PUT /api/me/password`, B-244).
 *
 * Here beside the device list because that is what it answers with: the
 * devices still signed in afterwards, and how many it signed out — counted
 * by the server, which is the only one that knows.
 */
export function changePassword(change: PasswordChangeInput): Promise<Reply<PasswordChanged>> {
  return send('PUT', '/me/password', change, PasswordChanged);
}
